/**
 * 月次設定(目標・前提値)。PL の前提になる 目標原価率・通話料単価・会社別アポ数目標などを月ごとに入れる。
 * 2026-09-24 発注者指示「設定はメニューバーの設定から各項目を設定できるように」で、
 * **設定 → 月次設定** のページ(`SettingsMonthlyPage`)と、PL 画面のボタン(モーダル)の両方から同じ入力を使う。
 *  - キャンセル率の入力は廃止(CXLをPLから外した。D-140)
 *  - 目標原価率は 給湯器 / 不用品 の2つ(これ以上で赤。既定 45% / 50%)
 */
import { useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import Modal from '@core/components/Modal';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import type { Company, MonthTargets } from '@core/lib/types';
import { saveMonthTargets } from './dashboardRepo';

export function MonthSettingsForm({
  month,
  targets,
  companies,
  onSaved,
  onCancel,
}: {
  month: string;
  targets: MonthTargets;
  companies: Company[];
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const [callRate, setCallRate] = useState(String(targets.call_rate_per_min));
  const [costTarget, setCostTarget] = useState(String(targets.cost_rate_total));
  const [costTargetFuyohin, setCostTargetFuyohin] = useState(String(targets.cost_rate_fuyohin));
  const [bonusRates, setBonusRates] = useState((targets.manager_bonus_rates ?? []).join(', '));
  const [timeeFee, setTimeeFee] = useState(String(targets.timee_fee_per_person ?? 220));
  const [companyTargets, setCompanyTargets] = useState<Record<string, string>>(Object.fromEntries(companies.map((c) => [c.id, String(targets.company_targets[c.id] ?? '')])));
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!user) return;
    setBusy(true);
    try {
      const call = Number(callRate);
      const ct = Number(costTarget);
      const ctf = Number(costTargetFuyohin);
      if (!Number.isFinite(call) || call < 0) throw new Error('通話料単価は0以上の数値で入力してください');
      if (!Number.isFinite(ct) || ct < 0) throw new Error('目標原価率(給湯器)は0以上の数値で入力してください');
      if (!Number.isFinite(ctf) || ctf < 0) throw new Error('目標原価率(不用品)は0以上の数値で入力してください');
      const rates = bonusRates
        .split(/[,、\s]+/)
        .map((s) => s.trim())
        .filter((s) => s !== '')
        .map((s) => Number(s.replace('%', '')));
      if (rates.some((r) => !Number.isFinite(r) || r < 0)) throw new Error('管理者報酬の率は0以上の数値(%)をカンマ区切りで入力してください');
      const fee = Number(timeeFee.replace(/[,円]/g, ''));
      if (!Number.isFinite(fee) || fee < 0) throw new Error('タイミー振込手数料は0以上の数値(円)で入力してください');
      const ct2: Record<string, number> = {};
      for (const c of companies) {
        const v = companyTargets[c.id]?.trim() ?? '';
        if (v === '') continue;
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0) throw new Error(`${c.name} の目標は0以上の数値で入力してください`);
        ct2[c.id] = n;
      }
      // 経費は「締め作業」の経費画面で入れる(P-22)。ここでは今の値をそのまま持ち回る
      await saveMonthTargets(
        month,
        { call_rate_per_min: call, cost_rate_total: ct, cost_rate_fuyohin: ctf, company_targets: ct2, expenses: targets.expenses, manager_bonus_rates: rates, timee_fee_per_person: fee },
        user.id,
      );
      toast('月次設定を保存しました');
      onSaved();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="form-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
        <div>
          <label>目標原価率・給湯器(%・これ以上で赤)</label>
          <input inputMode="decimal" value={costTarget} onChange={(e) => setCostTarget(e.target.value)} />
        </div>
        <div>
          <label>目標原価率・不用品(%・これ以上で赤)</label>
          <input inputMode="decimal" value={costTargetFuyohin} onChange={(e) => setCostTargetFuyohin(e.target.value)} />
        </div>
        <div>
          <label>Aj通話料単価(円/分)</label>
          <input inputMode="decimal" value={callRate} onChange={(e) => setCallRate(e.target.value)} />
        </div>
        <div>
          <label>管理者報酬の率(%・営業利益に対して。複数はカンマ区切り)</label>
          <input value={bonusRates} onChange={(e) => setBonusRates(e.target.value)} placeholder="10, 5" />
        </div>
        <div>
          <label>タイミー振込手数料(円・稼働1人1日あたり)</label>
          <NumInput value={timeeFee} onChange={setTimeeFee} placeholder="220" />
        </div>
      </div>

      <div className="gray-box">
        <div className="box-title">会社別アポ数目標(件/日)</div>
        <div className="form-grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          {companies.map((c) => (
            <div key={c.id}>
              <label>{c.name}</label>
              <NumInput value={companyTargets[c.id] ?? ''} onChange={(v) => setCompanyTargets((prev) => ({ ...prev, [c.id]: v }))} />
            </div>
          ))}
        </div>
      </div>

      <div className="gray-box">
        <div className="box-title">経費</div>
        <p className="hint" style={{ margin: 0 }}>
          経費は「締め作業」の<b>経費</b>画面で、日ごと・項目ごとに入れます(2026-09-10 指示: PL からは直接入力しない)。PL の経費はその入力値だけを使います。
        </p>
      </div>

      <div className="modal-actions">
        <button className="btn-green" disabled={busy} onClick={() => void save()}>
          保存する
        </button>
        {onCancel && (
          <button className="btn-line" onClick={onCancel}>
            キャンセル
          </button>
        )}
      </div>
    </>
  );
}

export default function MonthSettingsModal({
  month,
  targets,
  companies,
  onClose,
  onSaved,
}: {
  month: string;
  targets: MonthTargets;
  companies: Company[];
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <Modal onClose={onClose} width="min(860px, 94vw)">
      <h3>
        月次設定({month.slice(0, 4)}年{Number(month.slice(5))}月)
      </h3>
      <p className="hint" style={{ margin: '4px 0 12px' }}>PL日報の前提値。設定 → 月次設定 からも同じ内容を変更できます</p>
      <MonthSettingsForm month={month} targets={targets} companies={companies} onSaved={onSaved} onCancel={onClose} />
    </Modal>
  );
}
